import { useMemo, useRef, useState } from "react";
import { AlertCircle, ArrowRight, CheckCircle2, ClipboardList, Download, FileText, FolderOpen, HardDrive, Info, ShieldCheck, UploadCloud } from "lucide-react";
import { FaFileExcel, FaFilePdf } from "react-icons/fa";

import { API_INSURANCE_BULK } from "../../../config/Endpoints.js";
import { getAuthToken } from "../../../utils/Storage.js";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { postFormDataMultipart, postRawJson } from "../../utils/api.ts";
import styles from "./insurance-bulk-upload-page.module.css";

type Row = {
  rowNumber: number; email: string; employeeCode: string; relationship: string;
  cardNumber: string; insuredName: string; dob: string | null; gender: string;
  pdfName: string; validFrom: string; validTill: string;
};
type ParsedRow = Omit<Row, "pdfName" | "validFrom" | "validTill">;
type Result = {
  rowNumber: number; email: string; relationship: string; cardNumber: string;
  status: "READY" | "SUCCESS" | "FAILED" | "SKIPPED"; reason: string; employeeId?: number;
};

const heading = (value: unknown) => String(value ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const cell = (value: unknown) => String(value ?? "").trim();

function excelDate(value: unknown, parseDateCode: (value: number) => { y: number; m: number; d: number } | null): string | null {
  if (!value) return null;
  if (typeof value === "number") {
    const date = parseDateCode(value);
    return date ? `${date.y}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(2, "0")}` : null;
  }
  const raw = cell(value);
  const parts = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (parts) return `${parts[3]}-${parts[2].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

async function parseWorkbook(data: ArrayBuffer): Promise<ParsedRow[]> {
  const XLSX = await import("xlsx");
  const book = XLSX.read(data, { type: "array", raw: true });
  const sheet = book.Sheets[book.SheetNames[0]];
  if (!sheet) throw new Error("The workbook has no first sheet.");
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "" });
  const headers = (matrix[0] ?? []).map(heading);
  const index = (name: string) => headers.indexOf(name);
  for (const required of ["HEGICCARDNO", "EMAILID", "RELATIONSHIP", "INSUREDNAME"]) {
    if (index(required) < 0) throw new Error(`Missing Excel column: ${required}.`);
  }
  const get = (values: unknown[], name: string) => index(name) < 0 ? "" : values[index(name)];
  return matrix.slice(1).map((values, offset) => ({
    rowNumber: offset + 2,
    email: cell(get(values, "EMAILID")),
    employeeCode: cell(get(values, "EMPID")),
    relationship: cell(get(values, "RELATIONSHIP")),
    cardNumber: cell(get(values, "HEGICCARDNO")).toUpperCase(),
    insuredName: cell(get(values, "INSUREDNAME")),
    dob: excelDate(get(values, "DOB"), XLSX.SSF.parse_date_code),
    gender: cell(get(values, "INSUREDGENDER")),
  })).filter((row) => row.email || row.cardNumber || row.relationship);
}

async function exportResults(results: Result[]) {
  const XLSX = await import("xlsx");
  const sheet = XLSX.utils.json_to_sheet(results.map((row) => ({
    "Excel Row": row.rowNumber, Email: row.email, Relationship: row.relationship,
    "HEGIC Card No": row.cardNumber, Status: row.status, Reason: row.reason,
  })));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Upload Results");
  XLSX.writeFile(workbook, "insurance-upload-results.xlsx");
}

export default function InsuranceBulkUploadPage() {
  const excelInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [sourceRows, setSourceRows] = useState<ParsedRow[]>([]);
  const [excelName, setExcelName] = useState("");
  const [pdfs, setPdfs] = useState<Map<string, File>>(new Map());
  const [duplicatePdfNames, setDuplicatePdfNames] = useState<Set<string>>(new Set());
  const [validFrom, setValidFrom] = useState("");
  const [validTill, setValidTill] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [phase, setPhase] = useState<"upload" | "review" | "done">("upload");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [draggingExcel, setDraggingExcel] = useState(false);
  const [draggingPdfs, setDraggingPdfs] = useState(false);
  const rows = useMemo<Row[]>(() => sourceRows.map((row) => ({
    ...row, pdfName: pdfs.get(`${row.cardNumber}.PDF`)?.name ?? "", validFrom, validTill,
  })), [sourceRows, pdfs, validFrom, validTill]);
  const counts = useMemo(() => ({
    ready: results.filter((r) => r.status === "READY").length,
    success: results.filter((r) => r.status === "SUCCESS").length,
    skipped: results.filter((r) => r.status === "SKIPPED").length,
    failed: results.filter((r) => r.status === "FAILED").length,
  }), [results]);
  const matchedPdfCount = useMemo(() => sourceRows.filter((row) => row.cardNumber && pdfs.has(`${row.cardNumber}.PDF`)).length, [sourceRows, pdfs]);
  const missingPdfCount = sourceRows.length - matchedPdfCount;

  function choosePdfs(files: FileList | File[]) {
    if (!files.length) return;
    const next = new Map<string, File>();
    const duplicates = new Set<string>();
    Array.from(files).forEach((file) => {
      if (/\.pdf$/i.test(file.name)) {
        const key = file.name.toUpperCase();
        if (next.has(key)) duplicates.add(key);
        else next.set(key, file);
      }
    });
    setPdfs(next); setDuplicatePdfNames(duplicates); setResults([]); setPhase("upload"); setError("");
  }

  function resetForm() {
    setSourceRows([]); setExcelName(""); setPdfs(new Map()); setDuplicatePdfNames(new Set());
    setValidFrom(""); setValidTill(""); setResults([]); setPhase("upload"); setProgress(0); setError("");
    if (excelInputRef.current) excelInputRef.current.value = "";
    if (pdfInputRef.current) pdfInputRef.current.value = "";
  }

  async function chooseExcel(file?: File) {
    if (!file) return;
    setError(""); setResults([]); setPhase("upload"); setSourceRows([]); setExcelName("");
    try {
      const parsed = await parseWorkbook(await file.arrayBuffer());
      if (!parsed.length) throw new Error("No employee rows found in the first sheet.");
      setSourceRows(parsed); setExcelName(file.name);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not read Excel file."); }
  }

  async function validate() {
    if (!validFrom || !validTill || validTill < validFrom) { setError("Enter both batch dates in date order."); return; }
    if (!sourceRows.length || !pdfs.size) { setError("Select an Excel file and a folder of PDFs."); return; }
    setBusy(true); setError("");
    try {
      const { data: checked } = await postRawJson<Result[]>(`${API_INSURANCE_BULK}/validate`, getAuthToken("accessToken"), rows);
      const verified = await Promise.all(checked.map(async (item) => {
        if (item.status !== "READY") return item;
        const key = `${item.cardNumber.toUpperCase()}.PDF`;
        if (duplicatePdfNames.has(key)) return { ...item, status: "FAILED" as const, reason: "More than one PDF has this card filename." };
        const pdf = pdfs.get(key);
        if (!pdf) return { ...item, status: "FAILED" as const, reason: "Matching PDF is missing." };
        if (pdf.size > 5 * 1024 * 1024) return { ...item, status: "FAILED" as const, reason: "PDF exceeds 5 MB." };
        if (await pdf.slice(0, 5).text() !== "%PDF-") return { ...item, status: "FAILED" as const, reason: "File is not a valid PDF." };
        return item;
      }));
      setResults(verified); setPhase("review");
    }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Validation failed."); }
    finally { setBusy(false); }
  }

  async function apply() {
    setBusy(true); setError(""); setProgress(0);
    const final = [...results];
    const ready = final.map((item, index) => ({ item, index })).filter(({ item }) => item.status === "READY");
    const byEmployee = new Map<string, typeof ready>();
    ready.forEach((entry) => {
      const key = entry.item.email.trim().toLowerCase();
      byEmployee.set(key, [...(byEmployee.get(key) ?? []), entry]);
    });
    const groups = [...byEmployee.values()];
    let nextGroup = 0;
    let completed = 0;
    try {
      await Promise.all(Array.from({ length: Math.min(4, groups.length) }, async () => {
        while (nextGroup < groups.length) {
          const group = groups[nextGroup++];
          for (const { item, index } of group) {
            const row = rows.find((candidate) => candidate.rowNumber === item.rowNumber);
            const pdf = row && pdfs.get(`${row.cardNumber}.PDF`);
            if (!row || !pdf) final[index] = { ...item, status: "FAILED", reason: "Matching PDF is no longer available." };
            else {
              const data = new FormData();
              data.append("row", JSON.stringify(row)); data.append("pdf", pdf);
              try {
                const response = await postFormDataMultipart<Result>(`${API_INSURANCE_BULK}/apply`, getAuthToken("accessToken"), data);
                final[index] = response.data;
              }
              catch (caught) { final[index] = { ...item, status: "FAILED", reason: caught instanceof Error ? caught.message : "Upload failed." }; }
            }
            completed += 1;
            setResults([...final]); setProgress(completed);
          }
        }
      }));
      setPhase("done");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Upload stopped unexpectedly. Review results before retrying.");
    } finally {
      setBusy(false);
    }
  }

  return <PageShell className={styles.page}>
    <div className={styles.contentGrid}>
        <section className={styles.panel} aria-labelledby="upload-title">
          <div className={styles.sectionHeading}><span className={styles.sectionIcon}><UploadCloud size={19} /></span><div><h2 id="upload-title">1. Upload Excel & PDF Folder</h2><p>PDF filenames must match the HEGIC CARD NO in your spreadsheet.</p></div></div>

          <input ref={excelInputRef} className={styles.visuallyHidden} type="file" accept=".xlsx,.xls" aria-label="Choose employee Excel file" onChange={(event) => void chooseExcel(event.target.files?.[0])} />
          <div className={[styles.dropzone, draggingExcel && styles.dragging].filter(Boolean).join(" ")} onDragOver={(event) => { event.preventDefault(); setDraggingExcel(true); }} onDragLeave={() => setDraggingExcel(false)} onDrop={(event) => { event.preventDefault(); setDraggingExcel(false); void chooseExcel(event.dataTransfer.files[0]); }}>
            <span className={[styles.dropIcon, styles.excelIcon].join(" ")}><FaFileExcel size={33} aria-hidden="true" /></span>
            <div className={styles.dropContent}>
              <div className={styles.dropTop}>
                <div className={styles.dropText}><strong>Employee Insurance Excel</strong><span>Drop your .xlsx or .xls file here</span></div>
                <button type="button" className={styles.browseButton} onClick={() => excelInputRef.current?.click()}><UploadCloud size={17} /> Browse Excel File</button>
              </div>
              <div className={styles.dropMeta}><span><FileText size={14} /> Accepted: .xlsx, .xls</span><span><Info size={14} /> First worksheet is used</span></div>
              {excelName && <span className={styles.fileChip}><CheckCircle2 size={15} /> {excelName}</span>}
            </div>
          </div>

          <input ref={pdfInputRef} className={styles.visuallyHidden} type="file" accept=".pdf" multiple aria-label="Choose folder of card PDFs" onChange={(event) => choosePdfs(event.target.files ?? [])} {...{ webkitdirectory: "" }} />
          <div className={[styles.dropzone, draggingPdfs && styles.dragging].filter(Boolean).join(" ")} onDragOver={(event) => { event.preventDefault(); setDraggingPdfs(true); }} onDragLeave={() => setDraggingPdfs(false)} onDrop={(event) => { event.preventDefault(); setDraggingPdfs(false); choosePdfs(event.dataTransfer.files); }}>
            <span className={[styles.dropIcon, styles.pdfIcon].join(" ")}><FaFilePdf size={33} aria-hidden="true" /></span>
            <div className={styles.dropContent}>
              <div className={styles.dropTop}>
                <div className={styles.dropText}><strong>Insurance Card PDFs</strong><span>Drop PDF files here or select a folder</span></div>
                <button type="button" className={styles.browseButton} onClick={() => pdfInputRef.current?.click()}><FolderOpen size={17} /> Select PDF Folder</button>
              </div>
              <div className={styles.dropMeta}><span><FileText size={14} /> PDF files only</span><span><HardDrive size={14} /> Max. 5 MB each</span><span><Info size={14} /> Matched by HEGIC card number</span></div>
              {pdfs.size > 0 && <span className={styles.fileChip}><CheckCircle2 size={15} /> {pdfs.size} PDF {pdfs.size === 1 ? "file" : "files"} selected</span>}
            </div>
          </div>

          <div className={styles.datePanel}><div className={styles.dateTitle}><ShieldCheck size={17} /> Card validity for this batch</div><div className={styles.dateGrid}>
            <label>Valid From<input type="date" value={validFrom} onChange={(event) => { setValidFrom(event.target.value); setResults([]); setPhase("upload"); }} /></label>
            <label>Valid Till<input type="date" min={validFrom || undefined} value={validTill} onChange={(event) => { setValidTill(event.target.value); setResults([]); setPhase("upload"); }} /></label>
          </div></div>
        </section>

      <aside className={styles.panel} aria-label="Upload summary"><h2 className={styles.smallTitle}>Upload Summary</h2><div className={styles.summaryList}>
        <div><span><ClipboardList size={17} /> Employee Rows</span><strong>{sourceRows.length}</strong></div>
        <div><span><FileText size={17} /> PDF Files</span><strong>{pdfs.size}</strong></div>
        <div><span><CheckCircle2 size={17} /> Matched</span><strong>{matchedPdfCount}</strong></div>
        <div><span><AlertCircle size={17} /> Missing PDFs</span><strong>{missingPdfCount}</strong></div>
        {results.length > 0 && <><div><span><CheckCircle2 size={17} /> {phase === "done" ? "Successful" : "Ready to Upload"}</span><strong>{phase === "done" ? counts.success : counts.ready}</strong></div><div><span><AlertCircle size={17} /> Failed / Skipped</span><strong>{counts.failed + counts.skipped}</strong></div></>}
      </div><div className={styles.summaryStatus}><span className={styles.statusIcon}><ShieldCheck size={18} /></span><div><strong>Status</strong><small>{phase === "done" ? "Upload complete" : phase === "review" ? "Ready for review" : busy ? "Validating records" : sourceRows.length && pdfs.size ? "Ready to validate" : "Waiting for files"}</small></div><span className={styles.statusPill}>{phase === "done" ? "Complete" : phase === "review" ? "Review" : busy ? "Checking" : "Pending"}</span></div></aside>
    </div>

    {error && <div role="alert" className={styles.error}><AlertCircle size={17} /> {error}</div>}

    {results.length > 0 && <section className={styles.resultsPanel} aria-labelledby="results-title"><div className={styles.resultsHeading}><div><h2 id="results-title">{phase === "done" ? "Upload Results" : "Review Records"}</h2><p>Check the status and reason for every spreadsheet row.</p></div><button type="button" className={styles.secondaryButton} onClick={() => void exportResults(results)}><Download size={16} /> Download Results</button></div><div className={styles.resultCounts}><span>{results.length} total</span><span className={styles.countReady}>{counts.ready} ready</span><span className={styles.countSuccess}>{counts.success} successful</span><span className={styles.countSkipped}>{counts.skipped} skipped</span><span className={styles.countFailed}>{counts.failed} failed</span></div><div className={styles.tableScroll}><table><thead><tr><th>Excel Row</th><th>Email</th><th>Relationship</th><th>HEGIC Card No</th><th>Status</th><th>Reason</th></tr></thead><tbody>{results.map((item) => <tr key={item.rowNumber}><td>{item.rowNumber}</td><td>{item.email}</td><td>{item.relationship}</td><td>{item.cardNumber}</td><td><span className={[styles.resultBadge, styles[item.status.toLowerCase()]].join(" ")}>{item.status}</span></td><td>{item.reason || "—"}</td></tr>)}</tbody></table></div></section>}

    <div className={styles.actionBar}><button type="button" className={styles.textButton} disabled={busy} onClick={resetForm}>Start Over</button><div>{phase === "upload" && <button type="button" className={styles.primary} disabled={busy || !sourceRows.length || !pdfs.size || !validFrom || !validTill} onClick={() => void validate()}>{busy ? "Validating..." : "Validate & Continue"}<ArrowRight size={16} /></button>}{phase === "review" && <button type="button" className={styles.primary} disabled={busy || counts.ready === 0} onClick={() => void apply()}>{busy ? `Uploading ${progress}/${counts.ready}...` : `Confirm Upload (${counts.ready})`}<ArrowRight size={16} /></button>}{phase === "done" && <button type="button" className={styles.primary} onClick={() => void exportResults(results)}><Download size={16} /> Download Results</button>}</div></div>

  </PageShell>;
}
